/* ============================================================
   teg — API client.

   The dashboard's "token" is the dashboard password itself: the
   backend compares the bearer string to DASHBOARD_PASSWORD. So
   there is nothing to refresh, and a 401 means the password was
   rotated — the shell returns to the login screen rather than
   rendering half a page of nulls.
   ============================================================ */

const API = process.env.REACT_APP_API || '';

export class TegError extends Error {
  constructor(status, body) {
    super(body?.error || `Request failed (${status})`);
    this.status = status;
    this.body = body || {};
  }
}

let onUnauthorized = () => {};
export const setUnauthorizedHandler = (fn) => { onUnauthorized = fn; };

async function req(token, path, { method = 'GET', json, signal, timeout = 25000 } = {}) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeout);
  signal?.addEventListener('abort', () => ctrl.abort(), { once: true });
  let res;
  try {
    res = await fetch(`${API}/api${path}`, {
      method,
      signal: ctrl.signal,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(json ? { 'Content-Type': 'application/json' } : {}),
      },
      body: json ? JSON.stringify(json) : undefined,
    });
  } catch (e) {
    if (e.name === 'AbortError') throw new TegError(0, { error: 'The server took too long to answer' });
    throw new TegError(0, { error: 'Cannot reach the server' });
  } finally {
    clearTimeout(timer);
  }
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { error: text.slice(0, 200) }; }
  if (res.status === 401) { onUnauthorized(); throw new TegError(401, data); }
  if (!res.ok) throw new TegError(res.status, data);
  return data;
}

export const api = {
  login: (password) => req(null, '/auth', { method: 'POST', json: { password } }),
  stats: (t, signal) => req(t, '/stats', { signal }),
  services: (t, signal) => req(t, '/services', { signal }),
  processes: (t, signal) => req(t, '/processes', { signal }),
  hardware: (t) => req(t, '/hardware'),
  disks: (t) => req(t, '/disks'),
  diskUsage: (t) => req(t, '/disk', { timeout: 60000 }),
  journal: (t, unit, lines = 150) => req(t, `/journal/${unit}?lines=${lines}`),
  log: (t, name) => req(t, `/logs/${name}`),
  action: (t, action) => req(t, '/action', { method: 'POST', json: { action }, timeout: 30000 }),
  gpu: (t) => req(t, '/gpu'),
  checks: (t) => req(t, '/checks'),
  games: (t, signal) => req(t, '/games', { signal }),
  mcPacks: (t) => req(t, '/games/minecraft/packs'),
  mcStats: (t) => req(t, '/games/minecraft/stats', { timeout: 40000 }),
  mcHealth: (t) => req(t, '/games/minecraft/health'),
  terraria: (t) => req(t, '/games/terraria/status'),
  control: (t, game, action, extra = {}) =>
    req(t, `/games/${game}/control`, { method: 'POST', json: { action, ...extra }, timeout: 120000 }),
};

export { API };
